/**
 * Move these in? (slice 6d spec §7.2; Paper A3b): the recent chats with no
 * Area that Clef says belong in this one, and the docs (slice 6d PR 9), all
 * ticked, each with when it last changed. Move moves the ticked ones, a
 * chat's open tasks with it; Not now moves none. A dialog, a bottom sheet on
 * a phone. It opens after New area (`?move=1`), from the crew's "Made the
 * area" line, and from Move chats and docs in on the page.
 */
import { Button } from "@crew/ui/components/button";
import { Checkbox } from "@crew/ui/components/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@crew/ui/components/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@crew/ui/components/drawer";
import { Spinner } from "@crew/ui/components/spinner";
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { useState, type ReactNode } from "react";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { shortTime } from "../chats/format";
import { useMoveIn, useMoveInSuggestions, type Area } from "./api";
import { moveInSections, moveLabel, type MoveInRow } from "./area-view";

function Rows({
  rows,
  kind,
  off,
  onToggle,
  me,
}: {
  rows: MoveInRow[];
  kind: "chat" | "doc";
  off: ReadonlySet<string>;
  onToggle: (id: string) => void;
  me: Me;
}) {
  return (
    <ul className="flex flex-col divide-y divide-border rounded-xl ring-1 ring-border">
      {rows.map((r) => (
        <li key={r.id}>
          <label
            {...{ [`data-move-in-${kind}`]: r.title }}
            className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-accent"
          >
            <Checkbox checked={!off.has(r.id)} onCheckedChange={() => onToggle(r.id)} />
            <span className="min-w-0 flex-1 truncate text-body text-foreground">{r.title}</span>
            <span className="shrink-0 text-meta text-muted-foreground">
              {shortTime(r.at, me.workspace.timezone)}
            </span>
          </label>
        </li>
      ))}
    </ul>
  );
}

/** A list under its heading ("Chats", "Docs") when both have rows; alone, no heading. */
function Section({ heading, children }: { heading: string | null; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      {heading ? <h3 className="text-meta font-medium text-muted-foreground">{heading}</h3> : null}
      {children}
    </section>
  );
}

/** The window's body: loading, nothing to move, or the ticked list. */
function Body({
  query,
  off,
  onToggle,
  me,
  area,
}: {
  query: ReturnType<typeof useMoveInSuggestions>;
  off: ReadonlySet<string>;
  onToggle: (id: string) => void;
  me: Me;
  area: Area;
}) {
  if (query.error) return <p className="text-body text-brick">{errorHint(query.error)}</p>;
  if (!query.data) {
    return (
      <div className="flex items-center gap-2 py-6 text-meta text-muted-foreground">
        <Spinner className="size-4" />
        Looking through your recent chats and docs…
      </div>
    );
  }
  const sections = moveInSections(query.data);
  if (sections.length === 0) {
    return (
      <p data-move-in-empty className="py-4 text-body text-muted-foreground">
        No recent chats or docs look like they belong in {area.label}. Chats started here will be.
      </p>
    );
  }
  return (
    <div className="flex max-h-[50svh] flex-col gap-4 overflow-y-auto">
      {sections.map((s) => (
        <Section key={s.kind} heading={s.heading}>
          <Rows rows={s.rows} kind={s.kind} off={off} onToggle={onToggle} me={me} />
        </Section>
      ))}
    </div>
  );
}

function Window({
  open,
  onOpenChange,
  title,
  line,
  footer,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  line: string;
  footer: ReactNode;
  children: ReactNode;
}) {
  const mobile = useIsMobile();
  if (mobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange} showSwipeHandle>
        <DrawerContent className="pb-[env(safe-area-inset-bottom)]" data-move-in>
          <DrawerHeader>
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription>{line}</DrawerDescription>
          </DrawerHeader>
          <div className="px-4">{children}</div>
          <DrawerFooter>{footer}</DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]" data-move-in>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{line}</DialogDescription>
        </DialogHeader>
        {children}
        <DialogFooter>{footer}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Which suggestions are ticked: all of them, less the ones unticked. Chats' and docs' ids never clash. */
function useTicks(data: { suggestions: { id: string }[]; docs?: { id: string }[] } | undefined) {
  const [off, setOff] = useState<ReadonlySet<string>>(new Set());
  const on = (rows: { id: string }[] | undefined) =>
    (rows ?? []).filter((s) => !off.has(s.id)).map((s) => s.id);
  const ticked = { thread_ids: on(data?.suggestions), doc_ids: on(data?.docs) };
  const toggle = (id: string) =>
    setOff((was) => {
      const next = new Set(was);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  return { off, ticked, toggle };
}

function Footer({
  ticked,
  busy,
  onMove,
  onClose,
}: {
  ticked: { thread_ids: string[]; doc_ids: string[] };
  busy: boolean;
  onMove: () => void;
  onClose: () => void;
}) {
  return (
    <>
      <Button variant="ghost" onClick={onClose} disabled={busy}>
        Not now
      </Button>
      <Button
        data-move-in-go
        className="max-md:order-first"
        disabled={ticked.thread_ids.length + ticked.doc_ids.length === 0 || busy}
        onClick={onMove}
      >
        {busy ? "Moving…" : moveLabel(ticked.thread_ids.length, ticked.doc_ids.length)}
      </Button>
    </>
  );
}

export function MoveInWindow({
  area,
  me,
  open,
  onOpenChange,
}: {
  area: Area;
  me: Me;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const query = useMoveInSuggestions(area.id, open);
  const move = useMoveIn(area.id);
  const { off, ticked, toggle } = useTicks(query.data);
  const close = () => onOpenChange(false);
  return (
    <Window
      open={open}
      onOpenChange={onOpenChange}
      title="Move these in?"
      line={`Recent chats and docs that look like they belong in ${area.label}. A chat's open tasks move too.`}
      footer={
        <Footer
          ticked={ticked}
          busy={move.isPending}
          onMove={() => move.mutate(ticked, { onSuccess: close })}
          onClose={close}
        />
      }
    >
      <Body query={query} off={off} onToggle={toggle} me={me} area={area} />
      <MoveError error={move.error} />
    </Window>
  );
}

function MoveError({ error }: { error: unknown }) {
  return error ? <p className="text-meta text-brick">{errorHint(error)}</p> : null;
}
